import useTranslation from '../useTranslation';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { Props as DetailsViewEntityLinkProps } from 'components/DetailsViewEntityLink/types';

import useApplications from 'hooks/useApplications';
import useBoundary from 'hooks/useBoundary';
import useIsAdmin from 'hooks/useIsAdmin';
import usePageRoutes from 'hooks/usePageRoutes';
import useUser from 'hooks/useUser';

import {
    DETAIL_TYPE_EMAIL,
    DETAIL_TYPE_PHONE,
    DETAIL_TYPE_ROLE
} from 'pages/UserDetailsPage/constants';

import type { ApplicationResponse, Geometry, OrganizationMemberResponse } from './types';

const useData = () => {
    const { userId } = useParams();
    const { EDIT_USER } = useTranslation();
    const { fetchUser, isLoading } = useUser();
    const { usersPage } = usePageRoutes();
    const navigate = useNavigate();
    const [viewedUser, setViewedUser] = useState<OrganizationMemberResponse | undefined>(undefined);
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);
    const { isAdmin } = useIsAdmin();
    const { fetchBoundary } = useBoundary();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();

    const fetchViewedUser = async () => {
        if (userId) {
            const user = await fetchUser({ id: userId });

            setViewedUser(user ?? undefined);
            fetchApplications({ pilotId: userId, scheduledPilotId: userId }).next();
        }
    };

    useEffect(() => {
        if (userId) {
            fetchViewedUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? {
                      isEditable: false,
                      parts: boundary.boundaryParts.map(part => ({
                          boundary: part.boundary,
                          boundaryType: part.boundaryType,
                          geometryType: part.geometryType
                      }))
                  }
                : undefined
        );
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);
            fetchViewedUser();
        }
    };

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: viewedUser?.email ?? '',
            entityType: DETAIL_TYPE_EMAIL
        },
        {
            entityName: viewedUser?.phone ?? '',
            entityType: DETAIL_TYPE_PHONE
        },
        {
            entityName: viewedUser?.roles?.join(', ') ?? '',
            entityType: DETAIL_TYPE_ROLE
        }
    ];

    const dropdownMenuItems = isAdmin ? [{ onClick: handleEditClick, text: EDIT_USER }] : [];

    const handleNavigateBack = () => {
        navigate(usersPage);
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    return {
        applications,
        dropdownMenuItems,
        entitiesLink,
        handleClose,
        handleEditClick,
        handleEditClose,
        handleNavigateBack,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isAdmin,
        isApplicationsLoading,
        isApplicationsOpen,
        isClosing,
        isEditing,
        isLinkedEntitiesOpen,
        isLoading,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing,
        viewedUser
    };
};

export default useData;
